import React from "react";

// FreightKit ambient — the "Redesign 2" freight wallpaper, always-on and behind
// everything (mounted once at the app shell, position:fixed z-0):
//   1. Soft blur-blob aurora (the Lanura technique, glows on white).
//   2. Faint dashed flight-route arcs that flow, with twinkling hub dots.
//   3. Cargo planes / trucks / boxes that travel those routes (SVG animateMotion).
//   4. Larger cargo planes that drift right across the whole viewport.
// pointer-events-none + aria-hidden so it never touches content; all motion is
// transform/opacity only and freezes under prefers-reduced-motion.

const PLANE =
  "M62 32 L44 30 L34 14 L30 14 L36 29 L22 30 L14 22 L11 22 L15 30 L6 31 L6 33 L15 34 L11 42 L14 42 L22 34 L36 35 L30 50 L34 50 L44 34 L62 32 Z";

// TL pass: two routes only (was four) — one high sweep, one low counter-arc.
// Fewer lines reads calmer; the center-fade mask below keeps them at the margins.
const ROUTES = [
  "M-80 760 Q 500 300 1520 140",
  "M-80 640 Q 480 620 1520 300",
];

// Colours + opacities come from CSS vars so dark mode (see .dark .fkbg-root in
// motion.css) can lift the blue and dial the whole wallpaper back on navy.
const BLUE = "var(--fkbg-color)";

function FkAmbientCss({ className = "fixed", intensity = 1, calm = false }) {
  // Calm variant (working pages — admin/operator, quotes). TL review: the full
  // wallpaper still read "too heavy / a lot going on". So here we keep ONLY the
  // two off-canvas corner blooms — no flight routes, hub dots, travelling
  // freight, viewport planes, or the center orb that sat over content. The
  // blooms breathe very slowly (drift-calm: <=6px, no scale) and freeze under
  // prefers-reduced-motion. This is the "corner aurora" — nothing crosses the
  // content plane. Login/register/landing keep the full version below.
  if (calm) {
    return (
      <div
        aria-hidden
        className={`fkbg-root pointer-events-none inset-0 overflow-hidden ${className}`}
        style={{ opacity: intensity, "--fkbg-color": "#2563EB" }}
      >
        <div className="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent_0%,#000_7%,#000_92%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,#000_7%,#000_92%,transparent_100%)]">
          <div className="animate-drift-calm motion-reduce:animate-none absolute -right-32 -top-40 h-[36rem] w-[36rem] rounded-full bg-sky-300/25 blur-3xl dark:bg-sky-500/10" />
          <div className="animate-drift-slow-calm motion-reduce:animate-none absolute -bottom-44 -left-32 h-[40rem] w-[40rem] rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-500/10" />
        </div>
      </div>
    );
  }

  return (
    <div
      aria-hidden
      className={`fkbg-root pointer-events-none inset-0 overflow-hidden ${className}`}
      style={{
        opacity: intensity,
        "--fkbg-color": "#2563EB",
        "--fkbg-route-op": 0.6,
        "--fkbg-plane-op": 0.5,
        // TL pass: freight lives at the left/right margins and fades toward the
        // middle where the content sits ("fade into opaque") — a whisper remains.
        WebkitMaskImage:
          "linear-gradient(to right, #000 0%, #000 20%, rgba(0,0,0,0.15) 44%, rgba(0,0,0,0.15) 56%, #000 80%, #000 100%)",
        maskImage:
          "linear-gradient(to right, #000 0%, #000 20%, rgba(0,0,0,0.15) 44%, rgba(0,0,0,0.15) 56%, #000 80%, #000 100%)",
      }}
    >
      {/* 1. Blur-blob aurora */}
      <div className="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent_0%,#000_7%,#000_92%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,#000_7%,#000_92%,transparent_100%)]">
        <div className="animate-drift motion-reduce:animate-none absolute -right-32 -top-40 h-[36rem] w-[36rem] rounded-full bg-sky-300/35 blur-3xl dark:bg-sky-500/10" />
        <div className="animate-drift-slow motion-reduce:animate-none absolute -bottom-44 -left-32 h-[40rem] w-[40rem] rounded-full bg-blue-400/28 blur-3xl dark:bg-blue-500/10" />
        <div className="animate-float motion-reduce:animate-none absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-indigo-300/18 blur-3xl dark:bg-indigo-500/[0.08]" />
      </div>

      {/* 2 + 3. Flight routes, hubs, and freight travelling them */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        className="absolute inset-0"
        style={{ opacity: "var(--fkbg-route-op, 0.62)" }}
      >
        <g fill="none" stroke={BLUE} strokeLinecap="round">
          {ROUTES.map((d, i) => (
            <path
              key={i}
              d={d}
              strokeWidth={i < 2 ? 1.6 : 1.3}
              strokeDasharray={i < 2 ? "2 11" : "2 13"}
              style={{ animation: `fkbg-flow ${3 + i * 0.8}s linear infinite` }}
            />
          ))}
        </g>

        {/* hub dots — margins only (TL pass) */}
        <g fill={BLUE}>
          {[
            [70, 150, 3.2, "7s", "0s"],
            [1360, 760, 3.2, "7s", "2s"],
          ].map(([cx, cy, r, dur, delay], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              style={{ animation: `fkbg-hub ${dur} ease-in-out infinite`, animationDelay: delay }}
            />
          ))}
        </g>

        {/* freight in motion — one plane, one truck (TL pass: fewer things) */}
        <g fill={BLUE}>
          <g opacity="0.72">
            <animateMotion dur="24s" repeatCount="indefinite" rotate="auto" path={ROUTES[0]} />
            <path transform="translate(-16,-16) scale(0.5)" d={PLANE} />
          </g>
          <g opacity="0.6">
            <animateMotion dur="32s" repeatCount="indefinite" path={ROUTES[1]} />
            <g transform="translate(-13.2,-9) scale(0.6)">
              <path d="M2 22 V4 H26 V9 H35 L44 15 V22 Z" />
              <circle cx="11" cy="24" r="3" />
              <circle cx="37" cy="24" r="3" />
            </g>
          </g>
        </g>
      </svg>

      {/* 4. Cargo planes drifting across the whole viewport (TL pass: two) */}
      {[
        { anim: "fkbg-fly-a 62s linear infinite", size: 28, delay: "0s" },
        { anim: "fkbg-fly-c 96s linear infinite", size: 20, delay: "-54s" },
      ].map((p, i) => (
        <div
          key={i}
          className="fkbg-plane absolute left-0 top-0"
          style={{ color: BLUE, willChange: "transform, opacity", animation: p.anim, animationDelay: p.delay }}
        >
          <svg width={p.size} height={p.size} viewBox="0 0 64 64" fill="currentColor">
            <path d={PLANE} />
          </svg>
        </div>
      ))}
    </div>
  );
}

export default FkAmbientCss;
